import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UserProfileShareService } from '../../../services/shared-service/user-profile-share.service';
import { CLOUD_DATA_SERVICE, CloudDataService } from '../../../services/cloud/cloud-data.service';
import { AdminMandant, Employee, UserRightsDisableReasonModel } from '../../../models/cloud/cloud.model';
import { combineLatest, Subscription } from 'rxjs';
import { HideLoader, ShowLoader } from '../../store/loader/loader.actions';
import { Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { DeactivateDialogComponent } from './deactivate-dialog/deactivate-dialog.component';
import { TranslationDirective } from '../../shared-module/directives/translation.directive';
import { ActionHelper } from '../../../helper/action.helper';

@Component({
  selector: 'fi-user-rights',
  templateUrl: './user-rights.component.html',
  styleUrls: ['./user-rights.component.scss'],
  standalone: false,
})
export class UserRightsComponent implements OnInit, OnDestroy {
  employee: Employee;
  employees: Employee[] = [];
  mandant?: AdminMandant;
  subscription?: Subscription;
  isK2HelpSubscription?: Subscription;
  disableReason: UserRightsDisableReasonModel[] = [];
  isCloudUser = false;
  deactivatedUsers = new Set<number>();
  fetchedDeactivatedUsers = false;

  private readonly DEACTIVATE_SUBJECT = '%EMAIL_USER_MANAGEMENT_DEACTIVATE_USER_SUBJECT% <KKZ/>';
  private readonly DEACTIVATE_DESCRIPTION =
      `%EMAIL_USER_MANAGEMENT_GREETING%

%EMAIL_USER_MANAGEMENT_DEACTIVATE_USER_OPENING_TEXT%
%EMAIL_USER_MANAGEMENT_REQUESTOR% <CURRENT_USER_EMAIL/>

%EMAIL_USER_MANAGEMENT_AFFECTED_USER%

%EMAIL_USER_MANAGEMENT_NAME_USER%  <LAST_NAME/> <FIRST_NAME/>
%EMAIL_USER_MANAGEMENT_EMAIL_USER%  <EMAIL/>
%EMAIL_USER_MANAGEMENT_DEACTIVATE_USER_REASON%  <REASON/>
%EMAIL_USER_MANAGEMENT_NOTES%  <NOTE/>



%EMAIL_USER_MANAGEMENT_DEACTIVATE_USER_CLOSING_TEXT%

%EMAIL_USER_MANAGEMENT_REGARDS_1%
%EMAIL_USER_MANAGEMENT_REGARDS_2%`;


  readonly newUserUrl = `${environment.cloudUrl}?createEmployee=true`;

  constructor(
      @Inject(CLOUD_DATA_SERVICE) private cloudDataService: CloudDataService,
    private readonly sharedProfileService: UserProfileShareService,
    private store: Store,
    private router: Router,
    private matDialog: MatDialog,
  ) {
    cloudDataService.getUserRightsDisableReasons().subscribe(({ data, error, success }) => {
      if (success) {
        this.disableReason = data;
      }
    });
  }

  ngOnInit(): void {
    window.scrollTo(0, 0);

    this.subscription = combineLatest([this.sharedProfileService.getProfile(), this.sharedProfileService.getAdminMandant()])
        .subscribe(([profile, mandant]) => {
      if (mandant && profile) {
        const isPotentialCloudUser = profile.User.Login.endsWith('@finstral.cloud');

        if (isPotentialCloudUser) {
          this.updateDeactivatedUsers();
        }

        this.mandant = mandant;
        setTimeout(() => {
          this.subscription?.unsubscribe();
          this.subscription = null;
        });

        this.store.dispatch(new ShowLoader());

        this.cloudDataService.getEmployees(mandant.Id).subscribe(({success, data, error}) => {
          if (success) {
            this.employee = data.find(x => x.User.Login === profile.User.Login);
            this.employees = data.filter(x => x !== this.employee && x.Appplications?.find(app => app.HasAccess && app.Name === 'FINSTRALContacts'));
          }

          this.store.dispatch(new HideLoader());
          let c = 0;
          const interval = setInterval(() => {
            window.scrollTo(0, 0);
            if (++c > 40) {
              clearInterval(interval);
            }
          }, 20)
        })
      }
    })
  }

  private updateDeactivatedUsers(): void {
    this.cloudDataService.getK2HelpEmployeeData().subscribe(({ data, error, success }) => {
      this.fetchedDeactivatedUsers = true;
      if (success) {
        this.isCloudUser = true;
        this.deactivatedUsers.clear();

        for (const item of data) {
          if (item.IsDeactivated) {
            this.deactivatedUsers.add(item.EmployeesId)
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.store.dispatch(new HideLoader());
    this.subscription?.unsubscribe();
    this.isK2HelpSubscription?.unsubscribe();
  }

  onOpenUserDetails(employee: Employee): void {
    this.router.navigate(['/profileData-rights/' + employee.User.Id]);
  }

  onOpenDeactivateDialog(employee: Employee): void {
    if (this.fetchedDeactivatedUsers && this.disableReason.length) {
      const dialog = this.matDialog.open(DeactivateDialogComponent, {
        maxWidth: '613px',
        maxHeight: '80vh',
        width: '95%',
        autoFocus: 'none',
        data: { user: employee, disableReason: this.disableReason },
      });

      dialog.afterClosed().subscribe((data: {reasonId: number, text: string, reason: string}) => {
        if (!data) {
          return;
        }

        const translate = (text: string): string => {
          return text.replace(/%\w+%/gm, (match) => {
            const key = match.substring(1, match.length - 1);
            return TranslationDirective.data[key] ?? key;
          });
        }

        this.store.dispatch(new ShowLoader());
        this.cloudDataService.submitDeactivationTicket({
          UserId: employee.User.Id,
          Description: translate(this.DEACTIVATE_DESCRIPTION)
              .replace('<EMAIL/>', employee.User.Email)
              .replace('<FIRST_NAME/>', employee.User.FirstName)
              .replace('<LAST_NAME/>', employee.User.LastName)
              .replace('<REASON/>', data.reason)
              .replace('<CURRENT_USER_EMAIL/>', this.employee?.User?.Email ?? '')
              .replace('<NOTE/>', data.text),
          Subject: translate(this.DEACTIVATE_SUBJECT)
              .replace('<KKZ/>', this.mandant.KKZ),
        }).subscribe(({success}) => {
          this.store.dispatch(new HideLoader());

          if (success) {
            ActionHelper.showAction('GENERAL_MESSAGE', this.matDialog, {
              title: '%USER_MANAGEMENT_CREATE_USER_NOTIFCATION_HEADLINE%',
              content: `%USER_MANAGEMENT_CREATE_USER_NOTIFCATION_TEXT_4%<br/><br/>
                        %USER_MANAGEMENT_CREATE_USER_NOTIFCATION_TEXT_2%`
            });
            this.updateDeactivatedUsers();
            this.cloudDataService.getEmployees(this.mandant.Id).subscribe(({success, data, error}) => {
              if (success) {
                this.employee = data.find(x => x.User.Login === this.employee?.User.Login);
                this.employees = data.filter(x => x !== this.employee && x.Appplications?.find(app => app.HasAccess && app.Name === 'FINSTRALContacts'));
              }
            });
          }
        })
      });
    }
  }
}
